import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
  useTheme,
  LinearProgress,
} from '@mui/material';
import {
  Add,
  PlayArrow,
  Stop,
  Visibility,
  Delete,
  Science,
  TrendingUp,
  Settings,
  Assessment,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface Simulation {
  id: string;
  name: string;
  status: 'draft' | 'running' | 'completed' | 'failed';
  created_at: string;
  duration: number;
  components: number;
  progress?: number;
}

const SimulationDashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [openDialog, setOpenDialog] = useState(false);
  const [newSimName, setNewSimName] = useState('');
  const [newSimDuration, setNewSimDuration] = useState(30);
  const [simulations, setSimulations] = useState<Simulation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch simulations on mount
  useEffect(() => {
    fetchSimulations();
  }, []);

  const fetchSimulations = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.simulation.list();
      setSimulations(response.data);
    } catch (err: any) {
      // Silently handle 404 - endpoint not implemented yet
      if (err?.response?.status === 404) {
        console.log('Simulation list endpoint not yet implemented - showing empty state');
        setSimulations([]);
        setError(null);
      } else {
        setError(err.message || 'Failed to fetch simulations');
        console.error('Error fetching simulations:', err);
        setSimulations([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'running': return 'info';
      case 'draft': return 'default';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const handleCreateSimulation = async () => {
    try {
      setLoading(true);
      const config = {
        name: newSimName,
        duration: newSimDuration,
        time_step: 'day',
        stochastic: true,
      };

      const response = await api.simulation.create(config);
      console.log('Simulation created:', response.data);

      // Refresh the list
      await fetchSimulations();

      setOpenDialog(false);
      setNewSimName('');
      setNewSimDuration(30);
    } catch (err: any) {
      // Silently handle 404 - endpoint not implemented yet
      if (err?.response?.status === 404) {
        setError('Simulation creation not yet available. Try using Templates instead.');
      } else {
        setError(err.message || 'Failed to create simulation');
        console.error('Error creating simulation:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Digital Twin Simulations
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Model and simulate your supply chain network
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Science />}
            onClick={() => navigate('/simulation/templates')}
          >
            Templates
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpenDialog(true)}
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            }}
          >
            New Simulation
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                    {simulations.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Simulations
                  </Typography>
                </Box>
                <Science sx={{ fontSize: 40, color: alpha(theme.palette.primary.main, 0.3) }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.info.main }}>
                    {simulations.filter(s => s.status === 'running').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Running
                  </Typography>
                </Box>
                <PlayArrow sx={{ fontSize: 40, color: alpha(theme.palette.info.main, 0.3) }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                    {simulations.filter(s => s.status === 'completed').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completed
                  </Typography>
                </Box>
                <TrendingUp sx={{ fontSize: 40, color: alpha(theme.palette.success.main, 0.3) }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.secondary.main }}>
                    {simulations.reduce((sum, s) => sum + s.components, 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Components
                  </Typography>
                </Box>
                <Assessment sx={{ fontSize: 40, color: alpha(theme.palette.secondary.main, 0.3) }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Simulations Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Your Simulations
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Duration (days)</TableCell>
                  <TableCell align="right">Components</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {simulations.map((sim) => (
                  <TableRow key={sim.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {sim.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Chip
                          label={sim.status.toUpperCase()}
                          size="small"
                          color={getStatusColor(sim.status)}
                        />
                        {sim.status === 'running' && sim.progress && (
                          <Box sx={{ mt: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={sim.progress}
                              sx={{ height: 4, borderRadius: 2 }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {sim.progress}%
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{new Date(sim.created_at).toLocaleDateString('en-GB')}</TableCell>
                    <TableCell align="right">{sim.duration}</TableCell>
                    <TableCell align="right">{sim.components}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/simulation/${sim.id}`)}
                        title="View Details"
                      >
                        <Visibility />
                      </IconButton>
                      {sim.status === 'draft' && (
                        <IconButton size="small" color="primary" title="Run Simulation">
                          <PlayArrow />
                        </IconButton>
                      )}
                      {sim.status === 'running' && (
                        <IconButton size="small" color="warning" title="Stop Simulation">
                          <Stop />
                        </IconButton>
                      )}
                      {sim.status === 'completed' && (
                        <IconButton size="small" color="secondary" title="Configure">
                          <Settings />
                        </IconButton>
                      )}
                      <IconButton size="small" color="error" title="Delete">
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create Simulation Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Simulation</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Simulation Name"
              value={newSimName}
              onChange={(e) => setNewSimName(e.target.value)}
              sx={{ mb: 2 }}
              placeholder="e.g., Q4 Demand Forecast"
            />
            <TextField
              fullWidth
              type="number"
              label="Duration (days)"
              value={newSimDuration}
              onChange={(e) => setNewSimDuration(Number(e.target.value))}
              sx={{ mb: 2 }}
              helperText="How many days to simulate"
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Time Step</InputLabel>
              <Select defaultValue="day" label="Time Step">
                <MenuItem value="hour">Hour</MenuItem>
                <MenuItem value="day">Day</MenuItem>
                <MenuItem value="week">Week</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCreateSimulation}
            variant="contained"
            disabled={!newSimName}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SimulationDashboard;
